import React, { useEffect, useMemo, useState } from "react";
import { Plus, LayoutGrid, ListFilter, TrendingUp, AlertCircle, CheckCircle2, Clock, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import KanbanBoard from "../components/KanbanBoard";
import TaskModal from "../components/TaskModal";
import { STATUS, formatDate } from "../lib/utils";

const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

export default function DashboardPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [filterArea, setFilterArea] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [filterPriority, setFilterPriority] = useState("");

  const canEdit = user.role !== "lectura";
  const canCreate = user.role === "administrador" || user.role === "coordinador";

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, u, a, k, p] = await Promise.all([
        api.get("/tasks"), api.get("/users"), api.get("/areas"), api.get("/kpis"), api.get("/projects")
      ]);
      setTasks(t.data); setUsers(u.data); setAreas(a.data); setKpis(k.data); setProjects(p.data);
    } catch (e) {
      toast.error("Error al cargar el panel");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const areaMap = useMemo(() => Object.fromEntries(areas.map(a => [a.id, a.name])), [areas]);
  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u.name])), [users]);
  const coordSinColabs = user.role === "coordinador" && users.filter(u => u.role === "colaborador").length === 0;

  const filtered = tasks.filter(t => {
    if (filterArea && t.area_id !== filterArea) return false;
    if (filterAssignee && t.assignee_id !== filterAssignee) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    return true;
  });

  const onStatusChange = async (task, status) => {
    try {
      await api.put(`/tasks/${task.id}`, { status });
      setTasks(prev => prev.map(x => x.id === task.id ? { ...x, status, progress: status === "cumplido" ? 100 : x.progress } : x));
      toast.success("Estado actualizado");
      const { data } = await api.get("/kpis"); setKpis(data);
    } catch (e) { toast.error("Error al mover la tarjeta"); }
  };

  const openNew = () => { setCurrent(null); setOpenModal(true); };
  const openEdit = (t) => { setCurrent(t); setOpenModal(true); };
  const onSaved = () => { loadAll(); };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Panel de Control</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Dashboard KPI</h1>
          <p className="text-sm text-slate-500 mt-1">Control de trabajos pendientes y cumplimiento por área.</p>
        </div>
        {canCreate && (
          <button data-testid="new-task-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-crejpp hover:bg-crejpp-dark text-white font-semibold rounded-md px-4 py-2 text-sm">
            <Plus className="w-4 h-4" /> Nueva tarea
          </button>
        )}
        <ManualButton />
      </div>

      {coordSinColabs && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg px-4 py-3" data-testid="no-collabs-banner">
          No tiene colaboradores asignados. Comuníquese con el Administrador.
        </div>
      )}

      {/* KPI grid */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Total de tareas" value={kpis.total} icon={LayoutGrid} testid="kpi-total" />
          <KpiCard label="Cumplimiento global" value={`${kpis.completion_pct}%`} icon={CheckCircle2} accent="emerald" testid="kpi-completion" />
          <KpiCard label="Tareas vencidas" value={kpis.vencidas} icon={AlertCircle} accent="crejpp" testid="kpi-overdue" />
          <KpiCard label="Cumplidas a tiempo" value={kpis.a_tiempo} icon={Clock} accent="blue" testid="kpi-ontime" />
        </div>
      )}

      {/* Status counters */}
      {kpis && (
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Distribución por estado</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(STATUS).map(([k, meta]) => (
              <div key={k} className="p-3 border border-slate-200 rounded-md">
                <div className="flex items-center gap-2 text-xs text-slate-600 mb-1"><span className="status-dot" style={{background:meta.color}} />{meta.label}</div>
                <div className="font-heading text-2xl font-bold text-slate-900">{kpis.by_status[k] || 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bento charts */}
      {kpis && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ByAreaCard kpis={kpis} areas={areas} />
          <WorkloadCard kpis={kpis} users={users} />
          <TrendCard kpis={kpis} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
        <ListFilter className="w-4 h-4 text-slate-500" />
        <select value={filterArea} onChange={e => setFilterArea(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md bg-white" data-testid="filter-area">
          <option value="">Todas las áreas</option>
          {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md bg-white" data-testid="filter-assignee">
          <option value="">Todos los responsables</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md bg-white" data-testid="filter-priority">
          <option value="">Todas las prioridades</option>
          <option value="alta">Alta</option><option value="media">Media</option><option value="baja">Baja</option>
        </select>
        <div className="ml-auto text-xs text-slate-500">{filtered.length} de {tasks.length} tareas</div>
      </div>

      {/* Kanban */}
      {loading ? (
        <div className="text-center text-sm text-slate-500 py-16">Cargando tareas…</div>
      ) : (
        <KanbanBoard tasks={filtered} users={users} onCardClick={openEdit} onStatusChange={onStatusChange} canEdit={canEdit} />
      )}

      <TaskModal open={openModal} onClose={() => setOpenModal(false)} task={current} onSaved={onSaved} areas={areas} users={users} projects={projects} />
    </div>
  );
}

function ManualButton() {
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  const isAdmin = user.role === "administrador";

  const dl = async (role) => {
    setBusy(true); setShowMenu(false);
    try {
      const token = localStorage.getItem("crejpp_token");
      const url = role ? `${API}/manual?role=${role}` : `${API}/manual`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `manual_${role || user.role}_CRE-JPP.pdf`;
      link.click();
      URL.revokeObjectURL(objectUrl);
      toast.success("Manual descargado");
    } catch { toast.error("No se pudo descargar el manual"); }
    finally { setBusy(false); }
  };

  if (!isAdmin) {
    return (
      <button
        onClick={() => dl(null)}
        disabled={busy}
        data-testid="manual-btn"
        className="inline-flex items-center gap-2 border border-slate-300 hover:border-crejpp hover:text-crejpp text-slate-700 font-medium rounded-md px-4 py-2 text-sm bg-white transition-colors disabled:opacity-60"
      >
        <BookOpen className="w-4 h-4" /> {busy ? "Descargando…" : "Manual"}
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(v => !v)}
        disabled={busy}
        data-testid="manual-btn"
        className="inline-flex items-center gap-2 border border-slate-300 hover:border-crejpp hover:text-crejpp text-slate-700 font-medium rounded-md px-4 py-2 text-sm bg-white transition-colors disabled:opacity-60"
      >
        <BookOpen className="w-4 h-4" /> {busy ? "Descargando…" : "Manual"}
        <span className="text-xs text-slate-400">▾</span>
      </button>
      {showMenu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
          <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-md shadow-lg z-20 overflow-hidden" data-testid="manual-menu">
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-b border-slate-100 bg-slate-50">Descargar manual por rol</div>
            {[
              { k: "administrador", l: "Administrador" },
              { k: "coordinador",   l: "Coordinador" },
              { k: "colaborador",   l: "Colaborador" },
              { k: "lectura",       l: "Modo lectura" },
            ].map(o => (
              <button key={o.k} onClick={() => dl(o.k)} className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 hover:text-crejpp flex items-center gap-2" data-testid={`manual-opt-${o.k}`}>
                <BookOpen className="w-3.5 h-3.5 text-slate-400" /> {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent="slate", testid }) {
  const accentBg = { crejpp: "bg-red-50 text-crejpp", emerald: "bg-emerald-50 text-emerald-600", blue: "bg-blue-50 text-blue-600", slate: "bg-slate-100 text-slate-700" }[accent];
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accentBg}`}><Icon className="w-4 h-4" /></div>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="font-heading text-3xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function ByAreaCard({ kpis, areas }) {
  const areaMap = Object.fromEntries(areas.map(a => [a.id, a.name]));
  const rows = Object.entries(kpis.by_area || {}).slice(0,7).map(([id, v]) => ({ id, name: areaMap[id] || "—", ...v }));
  const max = Math.max(1, ...rows.map(r => r.total));
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 lg:col-span-1">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-4">Cumplimiento por área</div>
      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.id}>
            <div className="flex justify-between text-xs text-slate-700 mb-1">
              <span className="truncate max-w-[70%]">{r.name}</span>
              <span className="font-semibold">{r.pct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${r.pct}%` }} />
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-slate-400 py-6 text-center">Sin datos</div>}
      </div>
    </div>
  );
}

function WorkloadCard({ kpis, users }) {
  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
  const rows = Object.entries(kpis.workload || {}).sort((a,b) => b[1]-a[1]).slice(0,7).map(([id, count]) => ({ id, name: userMap[id] || "—", count }));
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-4">Carga de trabajo</div>
      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.id} className="flex items-center gap-3">
            <div className="w-32 truncate text-sm text-slate-700">{r.name}</div>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-crejpp" style={{ width: `${(r.count/max)*100}%` }} />
            </div>
            <div className="text-sm font-semibold text-slate-900 w-6 text-right">{r.count}</div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-slate-400 py-6 text-center">Sin tareas activas</div>}
      </div>
    </div>
  );
}

function TrendCard({ kpis }) {
  const entries = Object.entries(kpis.monthly_trend || {});
  const max = Math.max(1, ...entries.map(([,v]) => v));
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-3 h-3" /> Tendencia mensual (cumplidas)</div>
      <div className="flex items-end gap-2 h-32">
        {entries.map(([m, v]) => {
          const [y, mo] = m.split("-");
          return (
            <div key={m} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full bg-slate-100 rounded-t-sm flex items-end" style={{ height: "80%" }}>
                <div className="w-full bg-crejpp rounded-t-sm" style={{ height: `${(v/max)*100}%`, minHeight: v > 0 ? "4px" : "0" }} />
              </div>
              <div className="text-[10px] text-slate-500 font-medium">{MONTHS_ES[Number(mo)-1]}</div>
              <div className="text-xs font-semibold text-slate-800">{v}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
